<template>
  <div class="p-company-contacts-list">
    <contact-edit-form
      v-if="isEditFormShown"
      :value="getContactById(contactToEdit.id)"
      @submit="hideEditForm()"
      @cancel="hideEditForm()"
    />
    <el-table
      ref="table"
      :data="contactList"
      :default-sort="{ prop: 'name' }"
      border-top
      border-bottom
      row-key="id"
      :skeleton="isLoading"
      data-test-id="contact-list.table"
    >
      <el-table-column
        prop="name"
        :label="$t('profile.contact-list.name')"
        width="92"
        show-overflow-tooltip
      />
      <el-table-column
        prop="email"
        :label="$t('profile.contact-list.email')"
        width="146"
        show-overflow-tooltip
      />
      <el-table-column
        v-if="!isColumnHidden('phone')"
        prop="phone"
        :label="$t('profile.contact-list.phone')"
        width="146"
        show-overflow-tooltip
      />
      <el-table-column
        prop="type"
        :label="$t('profile.contact-list.type')"
        width="158"
        show-overflow-tooltip
      />
      <el-table-column
        v-if="!isColumnHidden('jobTitle')"
        prop="jobTitle"
        :label="$t('profile.contact-list.job-title')"
        width="96"
        show-overflow-tooltip
      />
      <el-table-column
        prop="user"
        :label="$t('profile.contact-list.user')"
        width="78"
        show-overflow-tooltip
      />
      <el-table-actions-column
        v-if="isEditModeEnabled"
        :show-columns-selector="false"
      >
        <template #default="{ row }">
          <el-dropdown
            placement="bottom-end"
            @command="(command) => handleAction(command, row)"
          >
            <el-button
              type="ghost"
              icon="i-ellipsis-h--16"
              :disabled="isEditDisabled || !!contactToEdit"
              data-test-id="contact-list.dropdown.toggle"
            />
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item
                  v-for="action in actions"
                  :key="action.command"
                  :command="action.command"
                  :data-test-id="`contact-list.dropdown.item-${action.command}`"
                >
                  {{ action.label }}
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </template>
      </el-table-actions-column>
    </el-table>
    <contact-delete-dialog
      :value="contactToDelete"
      :is-shown="isDeleteDialogShown"
      @hide="hideDeleteDialog"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { ContactType, isUserContact } from '@/app/domain/Contact';
import ContactEditForm from '@/app/components/profile/ContactEditForm';
import ContactDeleteDialog from '@/app/components/profile/ContactDeleteDialog';
import { getFullName } from '@/app/library/getFullName';

export default {
  name: 'ContactList',
  components: {
    ContactEditForm,
    ContactDeleteDialog
  },
  props: {
    value: {
      type: Array,
      default: () => []
    },
    hiddenColumns: {
      type: Array,
      default: () => [],
      validator: (value) => {
        return value.length
          ? value.filter((column) => ['phone', 'jobTitle'].includes(column))
          : true;
      }
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    isEditDisabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ['is-contact-editing'],
  data() {
    return {
      actions: [
        {
          label: this.$t('action.edit'),
          command: 'edit'
        },
        {
          label: this.$t('action.delete'),
          command: 'delete'
        }
      ],
      contactToEdit: null,
      contactToDelete: null,
      isDeleteDialogShown: false,
      isEditFormShown: false
    };
  },
  computed: {
    ...mapGetters('app', [
      'isEditModeEnabled'
    ]),
    contactList() {
      return this.value.map((contact) => ({
        id: contact.id,
        type: this.getType(contact),
        name: getFullName(contact.firstName, contact.lastName),
        email: contact.email,
        phone: contact.phone,
        jobTitle: contact.jobTitle,
        user: this.getUser(contact)
      }));
    }
  },
  watch: {
    isLoading(value) {
      if (value) {
        this.hideEditForm();
      }
    },
    isEditDisabled(value) {
      if (value) {
        this.hideEditForm();
      }
    }
  },
  methods: {
    handleAction(command, row) {
      switch (command) {
        case 'edit':
          this.handleEditAction(row);
          break;
        case 'delete':
          this.handleDeleteAction(row);
          break;
        default:
      }
    },
    handleEditAction(row) {
      this.contactToEdit = row;
      this.showEditForm();
    },
    handleDeleteAction(row) {
      this.contactToDelete = this.value.find((contact) => contact.id === row.id);
      this.showDeleteDialog();
    },
    showEditForm() {
      this.isEditFormShown = true;
      this.$emit('is-contact-editing', true);
    },
    hideEditForm() {
      this.contactToEdit = null;
      this.isEditFormShown = false;
      this.$emit('is-contact-editing', false);
    },
    showDeleteDialog() {
      this.isDeleteDialogShown = true;
    },
    hideDeleteDialog() {
      this.isDeleteDialogShown = false;
    },
    getContactById(id) {
      const contact = this.value.find((item) => item.id === id);
      return contact || {};
    },
    getType(contact) {
      return contact.type
        .filter((type) => [
          ContactType.Business,
          ContactType.Technical,
          ContactType.Billing
        ].includes(type))
        .map((type) => {
          const key = ContactType[type];
          return this.$t(`profile.contact-list.type.${key}`);
        })
        .join(', ');
    },
    getUser(contact) {
      return isUserContact(contact)
        ? this.$t('profile.contact-list.user.yes')
        : this.$t('profile.contact-list.user.no');
    },
    isColumnHidden(column) {
      return this.hiddenColumns.includes(column);
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@uikit/ui-kit/themes/acronis/src/common/variables';

.p-company-contacts-list {
  margin-bottom: -$av-border-width-sm;
}
</style>
